import type { AccountOption } from "@app/components/account-combobox";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import PnlCellPanel, { type PnlCellScope } from "@app/pages/pnl/pnl-cell-panel";
import { Suspense } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { diligenceCellPanel_Query } from "@app/queries/diligenceCellPanel_Query.graphql";

// Every cell on the diligence P&L drills through the ordinary /pnl panel, because
// it is the ordinary /pnl statement — read by year instead of by month. The rows
// are editable in place there, and a fix re-derives and moves the number you
// clicked, exactly as it does on /pnl.
//
// The combobox option lists that panel needs are fetched HERE, on open, instead
// of being added to the diligence page's own query — the diligence page loads a
// P&L, ARR and three retention tables already, and every visitor should not pay
// for the stakeholder list on the chance they click one row.
const optionsQuery = graphql`
  query diligenceCellPanel_Query {
    bankAccounts {
      id
      name
      nickname
      last4
    }
    allStakeholders {
      id
      name
    }
  }
`;

// The plumbing nodes (Root + per-account BASE) are never valid counterparties.
const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

export default function DiligenceCellPanel({
  scope,
  onClose,
  onChanged,
}: {
  scope: PnlCellScope;
  onClose: () => void;
  onChanged: () => void;
}) {
  return (
    <Suspense fallback={null}>
      <WithOptions scope={scope} onClose={onClose} onChanged={onChanged} />
    </Suspense>
  );
}

function WithOptions({
  scope,
  onClose,
  onChanged,
}: {
  scope: PnlCellScope;
  onClose: () => void;
  onChanged: () => void;
}) {
  const data = useLazyLoadQuery<diligenceCellPanel_Query>(
    optionsQuery,
    {},
    { fetchPolicy: "store-or-network" }
  );

  const accountOptions: AccountOption[] = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));
  const stakeholderOptions: StakeholderOption[] =
    data.allStakeholders.filter(isRealStakeholder);

  return (
    <PnlCellPanel
      scope={scope}
      accountOptions={accountOptions}
      stakeholderOptions={stakeholderOptions}
      onClose={onClose}
      onChanged={onChanged}
    />
  );
}
